import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import { currentProductAdderStatus, productAdderStepState, setStepNameAddStep } from '@src/stores/product';
import SpinnerBar from '@src/components/reusable/spinner/SpinnerBar';
import ProductAdderAdditionals from './ProductAdderAdditionals';
import ProductAdderSeatMap from './ProductAdderSeatMap';
import ProductAdderStatus from './ProductAdderStatus';
import ProductAdderTariff from './ProductAdderTariff';
import ProductAdderCalendar from './ProductAdderCalendar';
import { PackageQuantityStep } from '@src/features/productPackage';
import { device } from '@src/common/breakpoints';
import { addCartItemAddedSelector } from '@src/features/cart/store/cart';
import type { CartErrorsEnum } from '@src/domains/cart';
import ProductAdderPlanneSeatMapRender from './ProductAdderPlanneSeatMapRender';
import useFeatureFlag from '@src/hooks/useFeatureFlag';
import { shouldGoBackToTariffStep } from '@src/domains/tariffs/core/tariffVerificationRecovery';

interface ProductAdderBodyProps {
  isMultiProductFlow?: boolean;
}

const ProductAdderBody = (props: Readonly<ProductAdderBodyProps>) => {
  const { isMultiProductFlow = false } = props;
  const widgetId = useWidgetId();
  const dispatch = useAppDispatch();
  const { currentStep } = useAppSelector(productAdderStepState(widgetId));
  const currentAdderStatus = useAppSelector(currentProductAdderStatus(widgetId));
  const { errors } = useAppSelector(addCartItemAddedSelector);

  const rootRef = useRef<HTMLDivElement>(null);

  const { isFeatureFlagEnabled } = useFeatureFlag();

  const shouldRenderNewSeatmap = useMemo(() => isFeatureFlagEnabled('PR-3221-seat-map'), [isFeatureFlagEnabled]);

  const goBackToTariffStepIfNeeded = useCallback(() => {
    if (shouldGoBackToTariffStep(currentStep, errors as Record<string, CartErrorsEnum> | undefined)) {
      dispatch(setStepNameAddStep(widgetId, 'tariff'));
    }
  }, [currentStep, widgetId, errors, dispatch]);

  useEffect(() => {
    if (currentStep && currentStep !== 'calendar') {
      rootRef.current?.scrollIntoView();
    }
  }, [currentStep]);

  useEffect(() => {
    if (!errors) return;
    goBackToTariffStepIfNeeded();
  }, [errors, goBackToTariffStepIfNeeded]);

  return (
    <ProductBody ref={rootRef} $disablePadding={shouldRenderNewSeatmap && currentStep === 'seat_map'}>
      {currentStep === 'calendar' && <ProductAdderCalendar isMultiProductFlow={isMultiProductFlow} />}
      {currentStep === 'tariff' && <ProductAdderTariff isMultiProductFlow={isMultiProductFlow} />}
      {currentStep === 'package_quantity' && <PackageQuantityStep isMultiProductFlow={isMultiProductFlow} />}
      {currentStep === 'seat_map' &&
        (shouldRenderNewSeatmap ? (
          <ProductAdderPlanneSeatMapRender />
        ) : (
          <ProductAdderSeatMap isMultiProductFlow={isMultiProductFlow} />
        ))}
      {currentStep === 'additional' && <ProductAdderAdditionals isMultiProductFlow={isMultiProductFlow} />}
      {currentStep === 'finish' && <ProductAdderStatus />}
      {currentAdderStatus === 'adding_to_cart' ? (
        <SpinnerBarArea showBackdrop={!isMultiProductFlow}>
          <SpinnerBar />
        </SpinnerBarArea>
      ) : null}
    </ProductBody>
  );
};

const ProductBody = styled.div<{ $disablePadding: boolean }>`
  padding: ${({ $disablePadding }) => ($disablePadding ? '0px' : '8px')};
  position: relative;
  min-height: 100px;
  height: 100%;
  overflow-y: auto;

  @media (min-width: 375px) {
    padding: ${({ $disablePadding }) => ($disablePadding ? '0px' : '16px')};
  }

  ${device.smallTablet} {
    padding: ${({ $disablePadding }) => ($disablePadding ? '0px' : '24px')};
  }
`;

interface SpinnerBarAreaProps {
  showBackdrop: boolean;
}
const SpinnerBarArea = styled.div<SpinnerBarAreaProps>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.showBackdrop
      ? css`
          opacity: 0.7;
          background-color: #000000;
        `
      : ''}
`;
export default ProductAdderBody;
